import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiFetch } from '../apiClient/index';

type ApiQueryOptions<_TData> = {
  path: string;
  /** Force re-fetch even if data is fresh. */
  enabled?: boolean;
  /** Custom staleTime override (ms). Default: 30s from QueryClient defaults. */
  staleTime?: number;
};

export function useApiQuery<TData>(key: unknown[], options: ApiQueryOptions<TData>) {
  return useQuery<TData, Error>({
    queryKey: key,
    queryFn: async ({ signal }) => {
      const res = await apiFetch(options.path, { signal });
      if (!res.ok) {
        throw new Error(`API ${res.status}: ${options.path}`);
      }
      return res.json() as Promise<TData>;
    },
    enabled: options.enabled !== false,
    staleTime: options.staleTime,
  });
}

export function useApiMutation<TData, TVariables = void>(options: {
  path: string | ((vars: TVariables) => string);
  method?: string;
  onSuccess?: (data: TData, vars: TVariables) => void;
}) {
  const queryClient = useQueryClient();
  return useMutation<TData, Error, TVariables>({
    mutationFn: async (vars) => {
      const path = typeof options.path === 'function' ? options.path(vars) : options.path;
      const res = await apiFetch(path, {
        method: options.method ?? 'POST',
        body: vars !== undefined && vars !== null ? JSON.stringify(vars) : undefined,
      });
      if (!res.ok) {
        throw new Error(`API ${res.status}: ${path}`);
      }
      return res.json() as Promise<TData>;
    },
    onSuccess: (data, vars) => {
      options.onSuccess?.(data, vars);
      const path = typeof options.path === 'function' ? options.path(vars) : options.path;
      queryClient.invalidateQueries({ queryKey: [path.split('?')[0]] });
    },
  });
}

export { useQueryClient };
